import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  RealtimeChannel,
  RealtimePostgresChangesPayload,
  Session,
} from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase.ts';
import { consumePreviewLogin } from '@/lib/previewLogin.ts';
import { api, type AppConfig } from '@/lib/api.ts';
import type { Profile, Project, Task, TaskComment, TaskEvent, TaskPr } from '@shared/domain.ts';
import {
  emptyState,
  index,
  reducer,
  type DataState,
  type Installation,
  type InstallationRepo,
  type Invite,
  type Member,
  type ProjectSettings,
  type RowTable,
} from './store.ts';
import {
  advanceCursor,
  emptyCachedState,
  emptySyncCursors,
  readScopeCache,
  writeScopeCache,
} from './localCache.ts';

interface RiggValue {
  session: Session | null;
  me: Profile | null;
  projects: Project[];
  project: Project | null;
  setProject: (id: string) => void;
  /** True when `setProject(ALL_PROJECTS)` is the current selection — every
   *  screen below reads this rather than inferring it from `project === null`,
   *  because that is also the state before the first project has loaded. */
  allProjects: boolean;
  role: 'admin' | 'member';
  isAdmin: boolean;
  /** Role is per-project, not per-person — this is what a multi-project
   *  screen (the "All projects" board, the sidebar's counts) checks instead
   *  of the single-project `isAdmin` above. */
  isAdminOfProject: (projectId: string) => boolean;
  /** `isAdminOfProject`, addressed by the task rather than its project id —
   *  the shape every per-task caller (`visibleTasks`, `queueCounts`, a row's
   *  own `ctx.isAdmin`) actually wants. */
  isAdminOfTask: (task: Task) => boolean;
  /** Every project I am an admin of, whatever is on screen — what a
   *  subscription may be shared with. */
  adminProjectIds: string[];
  data: DataState;
  config: AppConfig | null;
  /** Load a task's comments and history. Cheap and idempotent. */
  openThread: (taskId: string) => void;
  /**
   * Show a task or comment before the server has confirmed it, so sending one
   * does not feel like it is waiting on the network. Pass a full row — same
   * shape a Realtime insert would carry — and remove it with the matching
   * `removeOptimistic*` once the real write settles, whichever way it goes.
   */
  addOptimisticTask: (task: Task) => void;
  removeOptimisticTask: (task: Task) => void;
  addOptimisticComment: (comment: TaskComment) => void;
  removeOptimisticComment: (comment: TaskComment) => void;
  /** Fetch the next, older page of a task's comments. */
  loadMoreComments: (taskId: string) => Promise<void>;
  refresh: () => void;
  signOut: () => Promise<void>;
}

const RiggContext = createContext<RiggValue | null>(null);

export function useRigg(): RiggValue {
  const value = useContext(RiggContext);
  if (!value) throw new Error('useRigg must be used inside <RiggProvider>');
  return value;
}

/**
 * The clock, on its own context.
 *
 * It ticks for the whole app — every "4m ago" and every live elapsed counter
 * reads it — but it used to live in `RiggValue` itself, which meant every
 * consumer of `useRigg()` re-rendered once a second whether or not it read
 * `now`, right down to whatever the person happened to be typing into at the
 * time. Splitting it out means only the handful of components that actually
 * call `useNow()` pay for the tick; a `NowProvider` that only ever changes
 * this one value, and passes `children` through unchanged, lets React bail
 * out of the rest of the tree instead of re-rendering it every second.
 */
const NowContext = createContext<number>(Date.now());

export function useNow(): number {
  return useContext(NowContext);
}

/**
 * How often the clock ticks. Nothing downstream needs sub-second precision —
 * the fastest-changing thing it drives is a live "12s" counter during a
 * task's first minute, and the queue math it also feeds (`needsYou`'s
 * staleness check) works in tens of minutes. A slower tick means the two
 * whole-project scans this ultimately triggers — `visibleTasks` and
 * `queueCounts`, both O(tasks) — run far less often as a project's task
 * count grows, instead of competing for the main thread every second
 * whether anyone is looking at a clock or not.
 */
const TICK_MS = 4000;

function NowProvider({ children }: { children: ReactNode }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  return <NowContext.Provider value={now}>{children}</NowContext.Provider>;
}

/** Every project-scoped table, and how Realtime should filter it. */
const PROJECT_TABLES: RowTable[] = [
  'tasks',
  'categories',
  'agents',
  'repos',
  'project_members',
  'project_invites',
  'intakes',
  'project_settings',
  'ci_runs',
  'task_comments',
  'task_events',
  'task_prs',
  'task_questions',
  'task_follow_ups',
];

const LAST_PROJECT_KEY = 'rigg:project';

/**
 * The sentinel `setProject` accepts in place of a real id, meaning "every
 * project I'm a member of, at once." It lives in the same slot a project id
 * would — `localStorage`, the `projectId` state — rather than as a separate
 * flag, so switching into and out of it is exactly as cheap as switching
 * between two projects.
 */
export const ALL_PROJECTS = 'all';

/** A page of comments, in either direction — small enough to feel instant. */
const COMMENT_PAGE_SIZE = 10;

export function RiggProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [me, setMe] = useState<Profile | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [adminProjectIds, setAdminProjectIds] = useState<string[]>([]);
  const [projectId, setProjectId] = useState<string | null>(
    () => localStorage.getItem(LAST_PROJECT_KEY),
  );
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [data, dispatch] = useReducer(reducer, emptyState);
  const [reloadKey, setReloadKey] = useState(0);

  /* ── auth ──────────────────────────────────────────────────────────── */

  useEffect(() => {
    // Subscribe before signing in, not after: the preview sign-in below
    // establishes a session, and a listener attached afterwards would miss the
    // change that matters most.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setAuthReady(true);
    });

    void (async () => {
      // A no-op in every build but a pull-request preview — see previewLogin.ts.
      await consumePreviewLogin();
      const { data: got } = await supabase.auth.getSession();
      setSession(got.session);
      setAuthReady(true);
    })();

    return () => sub.subscription.unsubscribe();
  }, []);

  /* ── who I am, and what I can see ──────────────────────────────────── */

  // Supabase's client refreshes the access token in the background —
  // `autoRefreshToken` — and every refresh delivers a *new* `Session` object
  // through `onAuthStateChange`, even though the signed-in user hasn't
  // changed. Keying effects on `session` itself means every silent refresh
  // (roughly hourly, more often if a tab was backgrounded) tears them down
  // and rebuilds them as though someone had just signed in. `userId` is the
  // part that actually identifies "who", so it's what belongs in a
  // dependency array; the token itself never needs to be read here — every
  // call to Supabase or the API already pulls the current one on its own.
  const userId = session?.user.id ?? null;

  useEffect(() => {
    if (!userId) {
      setMe(null);
      setProjects([]);
      setAdminProjectIds([]);
      dispatch({ type: 'reset' });
      return;
    }

    let cancelled = false;
    void (async () => {
      const [profile, memberships] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', userId).single(),
        supabase.from('project_members').select('project_id, role').eq('user_id', userId),
      ]);
      if (cancelled) return;

      setMe((profile.data as Profile | null) ?? null);

      const membershipRows = (memberships.data as { project_id: string; role: string }[] | null) ?? [];
      setAdminProjectIds(membershipRows.filter((m) => m.role === 'admin').map((m) => m.project_id));
      const ids = membershipRows.map((m) => m.project_id);
      if (ids.length === 0) {
        setProjects([]);
        return;
      }
      const { data: rows } = await supabase
        .from('projects')
        .select('*')
        .in('id', ids)
        .order('created_at', { ascending: true });
      if (cancelled) return;

      const list = (rows as Project[] | null) ?? [];
      setProjects(list);
      setProjectId((current) => {
        // "All projects" survives a membership refresh on its own terms — it
        // is never "not in the list" the way a deleted project's id would be.
        // It only collapses back to nothing once there is truly nothing left
        // to be "all" of.
        if (current === ALL_PROJECTS) return list.length ? ALL_PROJECTS : null;
        return current && list.some((p) => p.id === current) ? current : (list[0]?.id ?? null);
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, reloadKey]);

  useEffect(() => {
    if (!userId) return;
    api.config().then(setConfig).catch(() => setConfig(null));
  }, [userId]);

  useEffect(() => {
    if (projectId) localStorage.setItem(LAST_PROJECT_KEY, projectId);
  }, [projectId]);

  /* ── the project's data, and the subscription that keeps it current ──── */

  const allProjects = projectId === ALL_PROJECTS;
  // The project ids this screen is scoped to right now — one, or every
  // project the membership fetch above found. A stable string, not the array
  // itself, goes in the effect's dependency list below: the array is rebuilt
  // every render, but the *set* it names only actually changes when
  // membership does, and that is the only time the subscription needs to be
  // torn down and rebuilt.
  const scopeIds = allProjects ? projects.map((p) => p.id) : projectId ? [projectId] : [];
  const scopeKey = scopeIds.slice().sort().join(',');

  useEffect(() => {
    if (!userId || scopeIds.length === 0) {
      dispatch({ type: 'reset' });
      return;
    }

    let cancelled = false;
    let channel: RealtimeChannel | null = null;
    // `load` runs once up front and again on every reconnect (see the
    // `subscribe` callback below). Only the first run is a project switch —
    // clear `comments`/`events` there. Later runs are re-reads of the same
    // project and must leave whatever `openThread` has already fetched alone,
    // or a reconnect wipes the currently open task's thread out from under it.
    let firstLoad = true;

    // This scope's mirror of what's on disk: every table `load()` merges
    // into rather than replaces (`tasks`, `taskPrs`, `commentCounts`), plus
    // how far each has been read. Seeded from IndexedDB below when there is
    // something to seed it with — empty otherwise, which makes the first
    // fetch for a scope never seen on this device a full one, exactly as
    // before. Reused across every `load()` this effect runs (reconnects
    // included), so a socket drop after the initial read turns into a small
    // `gt` rather than a second full download of every task the project has
    // ever had.
    let known = emptyCachedState();
    let cursors = emptySyncCursors();
    const cacheKey = `${userId}:${allProjects ? ALL_PROJECTS : scopeKey}`;

    const load = async () => {
      // A single project filters with `eq`; "all projects" filters with `in`
      // over every project this browser is a member of. Either way this is a
      // bandwidth optimisation, not the security boundary — RLS restricts the
      // rows to ones the signed-in user could read regardless of what is
      // asked for here.
      const eq = (table: string, columns = '*') => {
        const query = supabase.from(table).select(columns);
        return allProjects ? query.in('project_id', scopeIds) : query.eq('project_id', scopeIds[0]!);
      };

      // `tasks`, `task_prs` and the comment-count read are the three that
      // scale with how much work a project has ever done — a project with a
      // few hundred tasks pays for all three on every mount, every project
      // switch and every socket reconnect. Once a cursor exists for this
      // scope, each becomes "rows changed since the last read" instead of
      // the whole table; see localCache.ts for why that merge is safe (none
      // of the three is ever hard-deleted from under it).
      const tasksQuery = cursors.tasks
        ? eq('tasks').gt('updated_at', cursors.tasks)
        : eq('tasks').order('created_at', { ascending: false });
      const taskPrsQuery = cursors.taskPrs
        ? eq('task_prs').gt('updated_at', cursors.taskPrs)
        : eq('task_prs').order('created_at', { ascending: true });
      const countsQuery = cursors.commentCounts
        ? eq('task_comments', 'id, task_id, created_at').gt('created_at', cursors.commentCounts)
        : eq('task_comments', 'id, task_id, created_at');

      const [
        tasks,
        categories,
        agents,
        subscriptions,
        repos,
        members,
        invites,
        intakes,
        settings,
        ciRuns,
        taskPrs,
        counts,
        installations,
        installationRepos,
        questions,
        followUps,
      ] = await Promise.all([
        tasksQuery,
        eq('categories').order('label'),
        eq('agents').order('created_at'),
        // Not filtered by project: a subscription can be shared into this one
        // from another, and RLS already limits it to the ones in a project of
        // mine — which is also what the share picker wants to know about.
        supabase.from('subscriptions').select('*').order('name'),
        eq('repos').order('owner'),
        eq('project_members'),
        eq('project_invites').is('accepted_at', null),
        eq('intakes').order('name'),
        // One row per project — always read as a set, even on a single
        // project, and indexed below by `project_id` rather than replacing a
        // single slot. A task on the "All projects" board still belongs to
        // exactly one project and still needs that project's own preview and
        // production workflow names to know whether it has anything to show.
        eq('project_settings'),
        eq('ci_runs').order('updated_at', { ascending: false }).limit(500),
        taskPrsQuery,
        // The thread itself is read when a task is opened, because holding
        // every comment on every task earns nothing — this is a count only,
        // and (once a cursor exists) only the comments that would change it.
        countsQuery,
        supabase.from('github_installations').select('*'),
        supabase.from('github_installation_repos').select('*'),
        // Both read whole on every load, newest first and capped — a handful
        // per task at most, so not worth a cursor of their own.
        eq('task_questions').order('created_at', { ascending: false }).limit(500),
        eq('task_follow_ups').order('created_at', { ascending: false }).limit(500),
      ]);
      if (cancelled) return;

      const memberRows = (members.data as unknown as Member[] | null) ?? [];
      const userIds = memberRows.map((m) => m.user_id);
      const { data: profileRows } = userIds.length
        ? await supabase.from('profiles').select('*').in('id', userIds)
        : { data: [] as Profile[] };
      if (cancelled) return;

      const newTasks = (tasks.data as unknown as Task[] | null) ?? [];
      const newTaskPrs = (taskPrs.data as unknown as TaskPr[] | null) ?? [];
      const newComments = (counts.data as unknown as { task_id: string; created_at: string }[] | null) ?? [];

      const commentCounts = { ...known.commentCounts };
      for (const row of newComments) {
        commentCounts[row.task_id] = (commentCounts[row.task_id] ?? 0) + 1;
      }

      known = {
        tasks: { ...known.tasks, ...index(newTasks) },
        categories: index((categories.data as never[] | null) ?? []),
        agents: index((agents.data as never[] | null) ?? []),
        subscriptions: index((subscriptions.data as never[] | null) ?? []),
        repos: index((repos.data as never[] | null) ?? []),
        members: Object.fromEntries(
          memberRows.map((m) => [`${m.project_id}:${m.user_id}`, m]),
        ),
        invites: index(((invites.data as unknown as Invite[] | null) ?? []) as never[]),
        intakes: index((intakes.data as never[] | null) ?? []),
        profiles: index(((profileRows as Profile[] | null) ?? []) as never[]),
        settings: Object.fromEntries(
          ((settings.data as unknown as ProjectSettings[] | null) ?? []).map((s) => [s.project_id, s]),
        ),
        ciRuns: index((ciRuns.data as never[] | null) ?? []),
        taskPrs: { ...known.taskPrs, ...index(newTaskPrs) },
        installations: Object.fromEntries(
          ((installations.data as unknown as Installation[] | null) ?? []).map((i) => [
            String(i.id),
            i,
          ]),
        ),
        installationRepos:
          (installationRepos.data as unknown as InstallationRepo[] | null) ?? [],
        commentCounts,
      };
      cursors = {
        tasks: advanceCursor(cursors.tasks, newTasks.map((t) => t.updated_at)),
        taskPrs: advanceCursor(cursors.taskPrs, newTaskPrs.map((p) => p.updated_at)),
        commentCounts: advanceCursor(cursors.commentCounts, newComments.map((c) => c.created_at)),
      };

      const payload: Partial<DataState> = {
        ...known,
        questions: index((questions.data as never[] | null) ?? []),
        followUps: index((followUps.data as never[] | null) ?? []),
      };
      if (firstLoad) {
        payload.comments = {};
        payload.events = {};
        firstLoad = false;
      }

      dispatch({ type: 'loaded', payload });

      // Fire-and-forget — see writeScopeCache's own doc comment for why a
      // failure here is never surfaced.
      void writeScopeCache(cacheKey, { cursors, data: known });
    };

    void (async () => {
      // A warm start, painted before the network read even begins: whatever
      // this scope looked like the last time it was open on this device,
      // which is almost always closer to "now" than an empty skeleton is.
      // `load()` below still runs regardless and is what's actually current —
      // this only changes what's on screen while that's in flight.
      const cached = await readScopeCache(cacheKey);
      if (cancelled) return;
      if (cached) {
        known = cached.data;
        cursors = cached.cursors;
        dispatch({ type: 'loaded', payload: known });
      }
      await load();
    })();

    /*
     * One channel, every project-scoped table, filtered server-side where
     * there is a single project to name. Realtime evaluates the same RLS
     * policies the initial select did, so a subscriber cannot be sent a row
     * they could not have read — the filter is a bandwidth optimisation, not
     * the security boundary. In "all projects" mode there is no single
     * `project_id` to filter on, so the binding is left unfiltered and RLS
     * alone decides what actually gets delivered — exactly the tables and
     * rows this browser's own `select`s above were allowed to read.
     *
     * The topic carries a nonce because two channels may briefly overlap: React
     * StrictMode mounts effects twice, and a project switch tears one down while
     * building the next. Reusing a topic name across that overlap leaves the
     * socket with a channel that reports SUBSCRIBED and delivers nothing — a
     * board that looks connected and never moves.
     */
    const topic = `rigg:project:${allProjects ? ALL_PROJECTS : scopeIds[0]}:${crypto.randomUUID().slice(0, 8)}`;
    channel = supabase.channel(topic);

    const onRow =
      (table: RowTable) =>
      (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
        if (payload.eventType === 'DELETE') {
          dispatch({ type: 'delete', table, row: payload.old as Record<string, unknown> });
        } else {
          dispatch({ type: 'upsert', table, row: payload.new as Record<string, unknown> });
        }
      };

    for (const table of PROJECT_TABLES) {
      channel.on(
        'postgres_changes',
        allProjects
          ? { event: '*', schema: 'public', table }
          : { event: '*', schema: 'public', table, filter: `project_id=eq.${scopeIds[0]}` },
        onRow(table),
      );
    }

    // Subscriptions are shared across projects, so a `project_id` filter would
    // miss a reading on one owned elsewhere — RLS alone decides, as above.
    channel.on('postgres_changes', { event: '*', schema: 'public', table: 'subscriptions' }, onRow('subscriptions'));

    // Profiles are not project-scoped, but a teammate changing their name
    // should still show up.
    channel.on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, onRow('profiles'));

    channel.subscribe((status) => {
      const live = status === 'SUBSCRIBED';
      dispatch({ type: 'live', live });
      // A dropped socket means we may have missed rows. Re-reading on
      // reconnect is the difference between "eventually consistent" and
      // "silently wrong until you reload".
      if (status === 'SUBSCRIBED') void load();
    });

    return () => {
      cancelled = true;
      if (channel) void supabase.removeChannel(channel);
    };
    // `scopeIds` itself is a fresh array every render — `scopeKey` is the
    // stable value that actually says whether the set of projects changed,
    // which is the only reason this effect needs to run again. `userId`,
    // not `session`, is what says whether *who's asking* changed — see the
    // comment above its declaration. Rerunning this on every silent token
    // refresh was what made comments vanish out from under an open task:
    // each rerun tears the channel down and rebuilds it with a fresh
    // `firstLoad`, which wipes `comments`/`events` back to `{}` without
    // `task.id` ever changing underneath TaskDetail's own effect — so
    // nothing re-fetches the thread until the task is deselected and
    // reselected, or the page is reloaded.
  }, [userId, allProjects, scopeKey, reloadKey]);

  /* ── a task's thread, on demand ────────────────────────────────────── */

  const loading = useRef(new Set<string>());

  const openThread = useCallback(
    (taskId: string) => {
      if (loading.current.has(taskId)) return;
      loading.current.add(taskId);
      void (async () => {
        // `finally`, because the guard above is what stops a second read while
        // one is in flight. A read that throws — a dropped connection, a tab
        // waking up — would otherwise leave the id in the set forever, and this
        // thread could never be read again for the life of the page.
        try {
          const [comments, events] = await Promise.all([
            // Newest first, capped — the detail pane opens on the latest
            // comment and reads older ones only on request. Reversed below so
            // the store still holds every thread oldest-first, which is what
            // `mergeThread` and the realtime merge in `store.ts` assume.
            supabase
              .from('task_comments')
              .select('*')
              .eq('task_id', taskId)
              .order('created_at', { ascending: false })
              .limit(COMMENT_PAGE_SIZE),
            supabase
              .from('task_events')
              .select('*')
              .eq('task_id', taskId)
              .order('id', { ascending: true })
              .limit(200),
          ]);
          dispatch({
            type: 'thread',
            taskId,
            comments: ((comments.data as unknown as TaskComment[] | null) ?? []).slice().reverse(),
            events: (events.data as unknown as TaskEvent[] | null) ?? [],
          });
        } finally {
          loading.current.delete(taskId);
        }
      })();
    },
    [],
  );

  /* ── optimistic writes ────────────────────────────────────────────────
     A task or comment a person just sent, shown before the server has
     confirmed it. It goes into the same maps a real row would, through the
     same `upsert`/`delete` actions Realtime uses — so it renders exactly like
     one, and removing it (the write landed, or it failed) is exactly as
     cheap as adding it. */
  const addOptimisticTask = useCallback((task: Task) => {
    dispatch({ type: 'upsert', table: 'tasks', row: task as unknown as Record<string, unknown> });
  }, []);
  const removeOptimisticTask = useCallback((task: Task) => {
    dispatch({ type: 'delete', table: 'tasks', row: task as unknown as Record<string, unknown> });
  }, []);
  const addOptimisticComment = useCallback((comment: TaskComment) => {
    dispatch({ type: 'upsert', table: 'task_comments', row: comment as unknown as Record<string, unknown> });
  }, []);
  const removeOptimisticComment = useCallback((comment: TaskComment) => {
    dispatch({ type: 'delete', table: 'task_comments', row: comment as unknown as Record<string, unknown> });
  }, []);

  const loadingMore = useRef(new Set<string>());

  const loadMoreComments = useCallback(
    async (taskId: string) => {
      if (loadingMore.current.has(taskId)) return;
      const held = data.comments[taskId];
      // Nothing loaded yet to page before — `openThread` covers that case.
      if (!held?.length) return;
      const oldest = held[0]!.created_at;
      loadingMore.current.add(taskId);
      try {
        const { data: rows } = await supabase
          .from('task_comments')
          .select('*')
          .eq('task_id', taskId)
          .lt('created_at', oldest)
          .order('created_at', { ascending: false })
          .limit(COMMENT_PAGE_SIZE);
        const older = ((rows as unknown as TaskComment[] | null) ?? []).slice().reverse();
        if (older.length) {
          // Reuses the same merge the initial read and the realtime stream
          // go through — the union is keyed by id, so this only ever adds.
          dispatch({ type: 'thread', taskId, comments: older, events: [] });
        }
      } finally {
        loadingMore.current.delete(taskId);
      }
    },
    [data.comments],
  );

  const project = useMemo(
    () => projects.find((p) => p.id === projectId) ?? null,
    [projects, projectId],
  );

  const role = useMemo<'admin' | 'member'>(() => {
    if (!me || !projectId) return 'member';
    return data.members[`${projectId}:${me.id}`]?.role ?? 'member';
  }, [data.members, me, projectId]);

  const isAdminOfProject = useCallback(
    (id: string) => Boolean(me && data.members[`${id}:${me.id}`]?.role === 'admin'),
    [data.members, me],
  );
  const isAdminOfTask = useCallback(
    (task: Task) => isAdminOfProject(task.project_id),
    [isAdminOfProject],
  );

  const value = useMemo<RiggValue>(
    () => ({
      session,
      me,
      projects,
      project,
      setProject: setProjectId,
      allProjects,
      role,
      isAdmin: role === 'admin',
      isAdminOfProject,
      isAdminOfTask,
      adminProjectIds,
      data,
      config,
      openThread,
      addOptimisticTask,
      removeOptimisticTask,
      addOptimisticComment,
      removeOptimisticComment,
      loadMoreComments,
      refresh: () => setReloadKey((k) => k + 1),
      signOut: async () => {
        await supabase.auth.signOut();
      },
    }),
    [
      session,
      me,
      projects,
      project,
      allProjects,
      role,
      isAdminOfProject,
      isAdminOfTask,
      adminProjectIds,
      data,
      config,
      openThread,
      addOptimisticTask,
      removeOptimisticTask,
      addOptimisticComment,
      removeOptimisticComment,
      loadMoreComments,
    ],
  );

  if (!authReady) return null;

  return (
    <RiggContext.Provider value={value}>
      <NowProvider>{children}</NowProvider>
    </RiggContext.Provider>
  );
}
